import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { collection, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase-config";

const PAGE_SIZE = 25;

/* =======================
   RANK BADGES (PAGE 1 ONLY)
======================= */
const rankBadge = (rank, pageNumber) =>
{
    if (pageNumber !== 1) return null;

    if (rank === 1) return "🏆";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    if (rank >= 4 && rank <= 10) return "⭐";
    if (rank >= 11 && rank <= 25) return "✨";

    return null;
};

const RatingLeaderboard = () =>
{
    const navigate = useNavigate();
    const { Std, page } = useParams();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const pageNumber = Number(page);

    /* -----------------------
       CANONICAL URL ENFORCER
    ------------------------ */
    useEffect(() =>
    {
        if (!page || isNaN(pageNumber) || pageNumber < 1)
        {
            navigate(`/leaderboard_rating/standard/${Std}/1`, { replace: true });
        }
    }, [page, pageNumber, Std, navigate]);

    /* -----------------------
       FETCH LEADERBOARD DATA
    ------------------------ */
    useEffect(() =>
    {
        if (!page || isNaN(pageNumber) || pageNumber < 1)
        {
            return;
        }

        let cancelled = false;

        const fetchLeaderboard = async () =>
        {
            setLoading(true);
            const tempRows = [];

            const startRank = (pageNumber - 1) * PAGE_SIZE + 1;
            const endRank = pageNumber * PAGE_SIZE;

            try
            {
                const StudentsRankRef = collection(
                    db,
                    "Leaderboard",
                    Std.toString(),
                    "Students"
                );

                for (let i = startRank; i <= endRank; i++)
                {
                    const snap = await getDoc(doc(StudentsRankRef, i.toString()));
                    if (!snap.exists())
                    {
                        break;
                    }
                    const data = snap.data();
                    tempRows.push({
                        Rank: i,
                        Handle: data.handle,
                        Rating: data.rating
                    });
                }

                if (!cancelled)
                {
                    setRows(tempRows);
                }
            }
            catch (err)
            {
                console.error("Failed to fetch leaderboard:", err);
                if (!cancelled)
                {
                    setRows([]);
                }
            }

            if (!cancelled)
            {
                setLoading(false);
            }
        };

        fetchLeaderboard();

        return () => { cancelled = true; };
    }, [Std, pageNumber]);

    if (loading)
    {
        return (
            <div className="mt-20 text-center text-xl sm:text-3xl">
                Loading leaderboard...
            </div>
        );
    }

    return (
        <div className="container mx-auto mt-6 px-2 sm:px-0">

            <h1 className="text-xl sm:text-3xl font-bold text-center mb-4 sm:mb-6">
                Standard {Std} — Ratings Leaderboard
            </h1>

            <div className="border-2 border-black rounded-lg p-3 sm:p-4 bg-white">

                <table className="w-full text-sm sm:text-lg">
                    <thead className="border-b-2 border-gray-300">
                        <tr>
                            <th className="py-2 text-center">Rank</th>
                            <th className="py-2 text-center">Handle</th>
                            <th className="py-2 text-center">Rating</th>
                        </tr>
                    </thead>

                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td
                                    colSpan={3}
                                    className="py-6 text-center text-gray-500"
                                >
                                    No users on this page
                                </td>
                            </tr>
                        )}

                        {rows.map(row => (
                            <tr
                                key={row.Rank}
                                className="border-b hover:bg-gray-50 transition-colors"
                            >
                                <td
                                    className={`py-2 font-medium text-center ${
                                        row.Rank === 1 ? "text-yellow-600" :
                                        row.Rank === 2 ? "text-gray-500" :
                                        row.Rank === 3 ? "text-orange-600" : ""
                                    }`}
                                >
                                    {rankBadge(row.Rank, pageNumber)} {row.Rank}
                                </td>

                                <td className="py-2 text-center">
                                    <Link
                                        to={`/profile/${row.Handle}`}
                                        className="font-medium text-blue-600 hover:underline"
                                    >
                                        {row.Handle}
                                    </Link>
                                </td>

                                <td className="py-2 text-center">
                                    {row.Rating}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* PAGINATION — ALWAYS HORIZONTAL */}
                <div className="mt-4 sm:mt-6 flex justify-between items-center gap-2">

                    <button
                        disabled={pageNumber === 1}
                        onClick={() =>
                            navigate(
                                `/leaderboard_rating/standard/${Std}/${pageNumber - 1}`
                            )
                        }
                        className="px-3 sm:px-4 py-2 border rounded disabled:opacity-55 text-sm sm:text-base"
                    >
                        Previous
                    </button>

                    <div className="flex items-center gap-2">
                        <span className="text-sm sm:text-lg">Page</span>
                        <input
                            type="number"
                            min="1"
                            value={pageNumber}
                            onChange={(e) =>
                            {
                                const p = Number(e.target.value);
                                if (p >= 1)
                                {
                                    navigate(
                                        `/leaderboard_rating/standard/${Std}/${p}`
                                    );
                                }
                            }}
                            className="w-16 sm:w-20 text-center border border-gray-400 rounded py-1"
                        />
                    </div>

                    <button
                        disabled={rows.length < PAGE_SIZE}
                        onClick={() =>
                            navigate(
                                `/leaderboard_rating/standard/${Std}/${pageNumber + 1}`
                            )
                        }
                        className="px-3 sm:px-4 py-2 border rounded disabled:opacity-55 text-sm sm:text-base"
                    >
                        Next
                    </button>

                </div>
            </div>
        </div>
    );
};

export default RatingLeaderboard;